import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, BellIcon, ChevronRightIcon, GraduationCapIcon, HouseIcon, LandmarkIcon, PlusIcon, SearchIcon, StoreIcon, WalletIcon } from 'lucide-react';
import { landingBImages } from '../../data/landingBVisual';
import { demoIdentity } from '../../data/landingVisual';

/**
 * Design B hero — a full-bleed campus photograph with the product sitting
 * inside the scene rather than beside it.
 *
 * The composition is deliberately the opposite of the other homepage: there,
 * the product is the subject and the campus is context; here the campus is the
 * subject and Campuzon is the layer over it. Everything overlaid is real
 * interface — the same schedule rows, tab bar and money panel the signed-in
 * product renders.
 */
const CAPABILITIES = [{
  icon: GraduationCapIcon,
  label: 'Academics',
  note: 'Your semester',
  to: '/academics'
}, {
  icon: HouseIcon,
  label: 'Housing',
  note: 'Find your place',
  to: '/housing'
}, {
  icon: LandmarkIcon,
  label: 'Campus',
  note: "What's happening",
  to: '/community'
}, {
  icon: StoreIcon,
  label: 'Marketplace',
  note: 'Buy. Sell. Discover.',
  to: '/market'
}];
const SCHEDULE = [{
  time: '09:00',
  title: 'Systems Engineering',
  note: 'Lecture · LT 100'
}, {
  time: '11:00',
  title: 'Project studio',
  note: 'Group work · CIT Lab 1'
}, {
  time: '14:00',
  title: 'Engineering Maths',
  note: 'Tutorial · LT 205'
}];
const TABS = [{
  icon: HouseIcon,
  label: 'Home',
  active: true
}, {
  icon: LandmarkIcon,
  label: 'Campus',
  active: false
}, {
  icon: WalletIcon,
  label: 'Wallet',
  active: false
}, {
  icon: BellIcon,
  label: 'Inbox',
  active: false
}];
export function HeroB() {
  const reduce = useReducedMotion();
  const rise = (delay: number) => ({
    initial: reduce ? false : {
      opacity: 0,
      y: 18
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: reduce ? 0 : 0.5,
      delay: reduce ? 0 : delay,
      ease: [0.23, 1, 0.32, 1]
    }
  });
  return <section className="relative isolate min-h-[42rem] w-full overflow-hidden lg:min-h-[46rem]" data-id="element-2434" data-mp="23vme59xpnuca">
      <img src={landingBImages.hero} alt="A student walking across a Nigerian university campus, using Campuzon on her phone"
    // The hero is the first thing painted, so it is never deferred.
    loading="eager" decoding="async" className="absolute inset-0 -z-10 h-full w-full object-cover object-[65%_center]" data-id="element-2435" data-mp="1yx1a0zfc8qik" />
      {/* Legibility, not decoration: the scrim exists so the headline holds contrast. */}
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-r from-black/80 via-black/45 to-black/25" data-id="element-2436" data-mp="1tyhmiul3j6o3" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 -z-10 h-40 bg-gradient-to-t from-black/60 to-transparent" data-id="element-2437" data-mp="1p77yfyf74beb" />

      <div className="mx-auto grid max-w-[88rem] gap-10 px-4 pb-14 pt-28 sm:px-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-center lg:pb-20 lg:pt-36" data-id="element-2438" data-mp="11gm419y46pms">
        {/* ------------------------------------------------- editorial column */}
        <div className="max-w-2xl" data-id="element-2439" data-mp="s455sdahj638">
          <motion.h1 {...rise(0)} className="text-[2.75rem] font-bold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-[4.25rem]" data-id="element-2440" data-mp="s56guhri3sky">
            Your campus.
            <br data-id="element-2441" data-mp="iebwjixtf325" />
            Your world.
          </motion.h1>

          <motion.p {...rise(0.08)} className="mt-6 max-w-xl text-h4 leading-relaxed text-white/85" data-id="element-2442" data-mp="1634s3eaiz8w8">
            One Campuzon account connects your academics, housing, campus life, marketplace and
            money.{' '}
            <span className="font-semibold text-brand-200" data-id="element-2443" data-mp="w4fa7limpofz">One account.</span>
          </motion.p>

          <motion.div {...rise(0.16)} className="mt-8 flex flex-wrap items-center gap-3" data-id="element-2444" data-mp="11308hfebly1s">
            <Link to="/register" className="group inline-flex min-h-touch-lg items-center gap-2 rounded-lg bg-brand-600 px-6 text-body font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-500 hover:text-white" data-id="element-2445" data-mp="3guehrtvrhvi2">
              Create your account
              <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-2446" data-mp="13ztshzc59up" />
            </Link>
            <Link to="/welcome/campuses" className="inline-flex min-h-touch-lg items-center gap-2 rounded-lg border border-white/35 bg-white/10 px-6 text-body font-semibold text-white backdrop-blur transition-colors duration-150 ease-out hover:bg-white/20 hover:text-white" data-id="element-2447" data-mp="3nec5an7a6mk1">
              <ChevronRightIcon aria-hidden className="h-4 w-4" data-id="element-2448" data-mp="ev4nz4jxp9xn" />
              Explore Campuzon
            </Link>
          </motion.div>

          {/* Four capabilities as entry points, not decoration. */}
          <motion.ul {...rise(0.24)} className="mt-10 grid gap-2.5 sm:grid-cols-2 lg:max-w-2xl xl:grid-cols-4" data-id="element-2449" data-mp="2zl3q2xeasosc">
            {CAPABILITIES.map(({
            icon: Icon,
            label,
            note,
            to
          }) => <li key={label} data-id="element-2450" data-mp="1a7w1s8yn99j1">
                <Link to={to} className="flex min-h-touch-lg items-center gap-3 rounded-xl border border-white/20 bg-black/35 px-3.5 py-2.5 backdrop-blur transition-colors duration-150 ease-out hover:border-white/45 hover:bg-black/50" data-id="element-2451" data-mp="1f55npemtr324">
                  <Icon aria-hidden className="h-5 w-5 shrink-0 text-brand-200" data-id="element-2452" data-mp="1ows8o2oysgi2" />
                  <span className="min-w-0" data-id="element-2453" data-mp="1tts99y31or1s">
                    <span className="block truncate text-caption font-semibold uppercase tracking-wide text-white" data-id="element-2454" data-mp="1xv43kgwv60gb">
                      {label}
                    </span>
                    <span className="block truncate text-caption text-white/70" data-id="element-2455" data-mp="27ttd9melwelh">{note}</span>
                  </span>
                </Link>
              </li>)}
          </motion.ul>
        </div>

        {/* ------------------------------------------------ product in scene */}
        <motion.div initial={reduce ? false : {
        opacity: 0,
        y: 26,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: reduce ? 0 : 0.6,
        delay: reduce ? 0 : 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="cz-preview mx-auto w-full max-w-sm lg:ml-auto lg:mr-0" aria-label="A preview of the Campuzon student app" role="img" data-id="element-2456" data-mp="dhwjvnwlsyns">
          <div className="overflow-hidden rounded-[1.5rem] border border-white/25 bg-surface shadow-[0_30px_70px_rgba(0,0,0,0.45)]" data-id="element-2457" data-mp="odyp85if7i4e">
            <div className="flex items-center justify-between border-b border-line px-4 py-3" data-id="element-2458" data-mp="kbbunwud7j3i">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-semibold text-brand-800" data-id="element-2459" data-mp="y41k6b93r65d">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-brand-700" data-id="element-2460" data-mp="t6rtytdw2qgh" />
                Campuzon
              </span>
              <span className="flex items-center gap-2 text-ink-subtle" data-id="element-2461" data-mp="1334nnr9fp4b8">
                <SearchIcon aria-hidden className="h-4 w-4" data-id="element-2462" data-mp="1311mcb2tzor7" />
                <BellIcon aria-hidden className="h-4 w-4" data-id="element-2463" data-mp="1mpzf7advb608" />
              </span>
            </div>

            <div className="px-4 pb-3 pt-4" data-id="element-2464" data-mp="1qoyk4uzln2ro">
              <p className="text-[13px] text-ink-muted" data-id="element-2465" data-mp="1cqgzj64zq6dz">Good afternoon,</p>
              <p className="text-[17px] font-bold text-ink" data-id="element-2466" data-mp="1cq8nfggoz53u">{demoIdentity.name.split(' ')[0]} 👋</p>

              <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-2467" data-mp="1hnqo6jkk2e0r">
                Today&rsquo;s schedule
              </p>
              <ul className="mt-2 space-y-2" data-id="element-2468" data-mp="17rnn7h1fwlfe">
                {SCHEDULE.map(slot => <li key={slot.time} className="flex items-center gap-3 rounded-lg border border-line bg-canvas px-3 py-2" data-id="element-2469" data-mp="u0s7bksix42b">
                    <span className="cz-num w-10 shrink-0 text-[11px] font-semibold text-brand-800" data-id="element-2470" data-mp="k232aslq7e3h">
                      {slot.time}
                    </span>
                    <span className="min-w-0" data-id="element-2471" data-mp="p04lren2dffh">
                      <span className="block truncate text-[13px] font-semibold text-ink" data-id="element-2472" data-mp="14jfgsv35ckzj">
                        {slot.title}
                      </span>
                      <span className="block truncate text-[11px] text-ink-muted" data-id="element-2473" data-mp="10f7q4sd4ouyo">{slot.note}</span>
                    </span>
                  </li>)}
              </ul>

              <p className="mt-3 flex items-center justify-between text-[11px] font-medium text-brand-800" data-id="element-2474" data-mp="qiwf05p9yb5t">
                View full timetable
                <ChevronRightIcon aria-hidden className="h-3.5 w-3.5" data-id="element-2475" data-mp="gtl54gukd2gv" />
              </p>
            </div>

            <div className="flex items-center justify-between border-t border-line px-2 py-2" data-id="element-2476" data-mp="cr8425qrhsqf">
              {TABS.map(({
              icon: Icon,
              label,
              active
            }) => <span key={label} className={`flex flex-1 flex-col items-center gap-0.5 text-[10px] ${active ? 'font-semibold text-brand-800' : 'text-ink-subtle'}`} data-id="element-2477" data-mp="7qk42ukk21k2">
                  <Icon aria-hidden className="h-4 w-4" data-id="element-2478" data-mp="3juwl0k90ju5x" />
                  {label}
                </span>)}
            </div>
          </div>

          {/* Money sits alongside as its own record, exactly as the product keeps it. */}
          <div className="mt-3 flex items-center gap-3 rounded-2xl border border-white/25 bg-surface px-4 py-3 shadow-[0_20px_44px_rgba(0,0,0,0.35)]" data-id="element-2479" data-mp="3ts9meimgf713">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-800" data-id="element-2480" data-mp="316hs1rf4y62h">
              <WalletIcon aria-hidden className="h-5 w-5" data-id="element-2481" data-mp="364t2a39ceil8" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-2482" data-mp="3b2l2rlo1zd11">
              <span className="block text-[11px] text-ink-muted" data-id="element-2483" data-mp="3b1rtoe1mgbfq">Recorded balance</span>
              <span className="cz-num block text-[17px] font-bold text-ink" data-id="element-2484" data-mp="3fwfmuq7klu0n">₦45,000.00</span>
            </span>
            <span className="inline-flex min-h-[32px] shrink-0 items-center gap-1 rounded-lg bg-brand-700 px-3 text-[11px] font-semibold text-white" data-id="element-2485" data-mp="2id4dgwvqcc91">
              <PlusIcon aria-hidden className="h-3.5 w-3.5" data-id="element-2486" data-mp="109k0cqhcan9l" />
              Add money
            </span>
          </div>
          <p className="mt-2 text-caption text-white/70" data-id="element-2487" data-mp="157lin96xyik0">
            Demonstration account. Campuzon keeps the record; licensed providers move the money.
          </p>
        </motion.div>
      </div>
    </section>;
}